import React, { useState, useEffect, useCallback } from 'react';
import { doc, setDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useData } from '../contexts/DataContext';
import { formatUserName } from '../utils/industryConfig';
import { useNotification } from '../contexts/NotificationContext';
import { useAuth } from '../contexts/AuthContext';
import { 
  loadTeamsAndUsers, 
  inviteUser, 
  updateUserTemplate, 
  updateUserRole,
  bulkAssignTemplate,
  removeUserFromTeam,
  findUserTeam,
  getRoleDisplayName,
  getStatusDisplayName,
  getStatusBadge,
  getRoleBadge,
  getActualUserStatus,
  checkAgentLimit
} from '../utils/teamManagement';
import * as firestorePaths from '../utils/firestorePaths';
import { getArtifactProjectId } from '../utils/firestorePaths';
import UserInviteForm from './shared/UserInviteForm';
import TemplateSelector from './shared/TemplateSelector';
// TeamSelector removed - userTeam now auto-switches via AuthContext
import LoadingSpinner from './shared/LoadingSpinner';
import Button from './shared/Button';
import Card from './shared/Card';
// PageHeader removed - title is in App.js like other pages
import EmptyState from './shared/EmptyState';
import ConfirmDialog from './shared/ConfirmDialog';
import { colors, spacing, typography } from '../styles/designTokens';

const TeamManager = ({ onTeamCreated, userRole, userTeam, db, firebaseConfig, getEmailDocumentId, userEmail, isAdmin, canManageUsers, availableScripts = {} }) => {
  // Get viewing state from AuthContext
  const { isViewingOtherTeam, userTeamName } = useAuth();
  
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false);
  // userTeam now auto-switches when admin views another team
  const selectedTeam = userTeam;
    
  // Initialize Firebase Functions
  const functions = getFunctions();
  const refreshUserClaimsByEmail = httpsCallable(functions, 'refreshUserClaimsByEmail');
    
  // Use DataContext for comprehensive template data
  const { templates: dataContextTemplates, isLoadingData } = useData();
    
  // Use notification system (replaces alert())
  const { showSuccess, showError, showWarning, showInfo } = useNotification();
    
  // Combine templates from DataContext and availableScripts prop
  const allTemplates = React.useMemo(() => {
    const combined = { ...availableScripts };
        
    // Add templates from DataContext
    if (dataContextTemplates && Object.keys(dataContextTemplates).length > 0) {
      Object.entries(dataContextTemplates).forEach(([id, template]) => {
        combined[id] = template;
      });
    }
        
    console.log('🔍 TeamManager combined templates:', {
      availableScriptsCount: Object.keys(availableScripts).length,
      dataContextCount: Object.keys(dataContextTemplates || {}).length,
      combinedCount: Object.keys(combined).length,
      templateNames: Object.values(combined).map(t => t.name).sort(),
      templateIds: Object.keys(combined).sort(),
      combinedTemplateDetails: Object.entries(combined).map(([id, t]) => ({
        id, 
        name: t.name, 
        creatorId: t.creatorId
      }))
    });
        
    return combined;
  }, [availableScripts, dataContextTemplates]);

    
  // Template assignment state
  const [showTemplateAssignment, setShowTemplateAssignment] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [bulkTemplate, setBulkTemplate] = useState('example-script');
    
  // Filter state
  const [filters, setFilters] = useState({
    email: '',
    role: '',
    status: '',
    team: ''
  });
    
  // Warning dialog state
  const [showWarningDialog, setShowWarningDialog] = useState(false);
  const [warningAction, setWarningAction] = useState(null);
  const [warningData, setWarningData] = useState({});
  const [missingTemplates, setMissingTemplates] = useState([]);
  
  // Agent limit state
  const [agentLimitInfo, setAgentLimitInfo] = useState(null);

  // Inline editing state (9A role, 9B template)
  const [editingRoleUserId, setEditingRoleUserId] = useState(null);
  const [editingTemplateUserId, setEditingTemplateUserId] = useState(null);

  // Deactivation confirmation state (9C)
  const [showDeactivateConfirm, setShowDeactivateConfirm] = useState(false);
  const [userToDeactivate, setUserToDeactivate] = useState(null);

  // Load users and teams - only show users from current user's team
  const loadData = useCallback(async () => {
    if (!db || !userRole) {
      console.log('TeamManager - Cannot load data:', { db: !!db, userRole });
      return;
    }
        
    console.log('TeamManager - Starting to load data for role:', userRole);
    setLoading(true);
    try {
      const { teams: teamsData, users: usersData } = await loadTeamsAndUsers(db, firebaseConfig, userRole, userEmail, userTeam);
            
      // Set teams and users - utility function already filters appropriately
      setTeams(teamsData);
      setUsers(usersData);
            
      // Debug logging to see what's being loaded
      console.log('TeamManager - Data loaded successfully:', {
        userRole,
        userEmail,
        userTeam,
        teamsLoaded: teamsData.length,
        usersLoaded: usersData.length,
        teamIds: teamsData.map(t => t.id),
        userEmails: usersData.map(u => u.email),
        userTeamIds: usersData.map(u => u.teamId).filter(Boolean)
      });
            
      // Debug user template assignments
      // BACKWARD COMPAT: Check both defaultTemplate (new) and assignedTemplate (old)
      console.log('🔍 TeamManager - User template assignments:', usersData.map(user => {
        const templateId = user.defaultTemplate || user.assignedTemplate;
        return {
          email: user.email,
          role: user.role,
          teamId: user.teamId,
          templateId: templateId,
          hasTemplate: !!templateId,
          isExampleScript: templateId === 'example-script'
        };
      }));
            
      // Check for missing templates
      // BACKWARD COMPAT: Check both defaultTemplate and assignedTemplate
      const missingTemplatesList = usersData
        .filter(user => {
          const templateId = user.defaultTemplate || user.assignedTemplate;
          return templateId && templateId !== 'example-script' && !allTemplates[templateId];
        })
        .map(user => ({ email: user.email, missingTemplateId: user.defaultTemplate || user.assignedTemplate }));
            
      setMissingTemplates(missingTemplatesList);
            
      // Log missing templates for debugging (but don't auto-fix to avoid loops)
      if (missingTemplatesList.length > 0) {
        console.warn('⚠️ Some users have templates not found in current template list:', missingTemplatesList);
        console.warn('Available template IDs:', Object.keys(allTemplates));
        // Note: Auto-fix removed to prevent infinite reload loops
        // Templates will show as "Example Script (Demo)" if not found
      }
      
      // Load agent limit info for the current team
      const effectiveTeam = userTeam || (teamsData.length > 0 ? teamsData[0].id : null);
      if (effectiveTeam) {
        try {
          const limitInfo = await checkAgentLimit(db, effectiveTeam);
          setAgentLimitInfo(limitInfo);
          console.log('📊 Agent limit info:', limitInfo);
        } catch (limitError) {
          console.warn('Could not load agent limit:', limitError);
        }
      }
            
    } catch (error) {
      console.error('Error loading users and teams:', error);
      showError(`Failed to load team data. ${error.message}`);
    } finally {
      setLoading(false);
      setHasInitialized(true);
    }
  // CRITICAL: userTeam must be in deps so loadData refreshes when admin switches viewed team
  // NOTE: firebaseConfig removed - loadTeamsAndUsers uses getArtifactProjectId() now
  // NOTE: allTemplates intentionally NOT in deps - it changes as DataContext loads and would cause loops
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [db, userRole, userEmail, userTeam]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Debug: Log props when component mounts
  useEffect(() => {
    console.log('TeamManager - Component props:', {
      userRole,
      userEmail,
      userTeam,
      isAdmin: isAdmin ? 'function' : 'undefined',
      canManageUsers: canManageUsers ? 'function' : 'undefined'
    });
  }, [userRole, userEmail, userTeam, isAdmin, canManageUsers]);

  // Warning dialog functions
  const showTemplateWarning = (action, data) => {
    setWarningAction(action);
    setWarningData(data);
    setShowWarningDialog(true);
  };

  const executeWarningAction = async () => {
    setShowWarningDialog(false);
        
    switch (warningAction) {
      case 'updateUserTemplate':
        await handleUpdateUserTemplate(warningData.userId, warningData.newTemplate);
        break;
      case 'bulkAssignTemplate':
        await handleBulkAssignTemplate();
        break;
      default:
        break;
    }
  };

  // Last successful invite signup link (shown after invitation)
  const [lastInviteLink, setLastInviteLink] = useState(null);
  const [lastInviteEmail, setLastInviteEmail] = useState(null);
  const [linkCopied, setLinkCopied] = useState(false);

  // Per-row copy-invite-link state: { [email]: 'idle' | 'loading' | 'copied' }
  const [copyInviteState, setCopyInviteState] = useState({});

  // Generate a fresh invite link for a pending user and copy to clipboard
  const handleCopyInviteLink = useCallback(async (user) => {
    const email = user.email || user.id;
    setCopyInviteState(prev => ({ ...prev, [email]: 'loading' }));
    try {
      const sendInvitation = httpsCallable(getFunctions(), 'sendAgentInvitation', { timeout: 600000 });
      const result = await sendInvitation({
        email,
        teamId: selectedTeam,
        teamName: userTeamName || selectedTeam
      });
      const url = result.data?.signupUrl;
      if (url) {
        await navigator.clipboard.writeText(url);
        setCopyInviteState(prev => ({ ...prev, [email]: 'copied' }));
        showSuccess('Invite link copied to clipboard!');
        setTimeout(() => setCopyInviteState(prev => ({ ...prev, [email]: 'idle' })), 3000);
      } else {
        showError('Could not generate invite link. Please try again.');
        setCopyInviteState(prev => ({ ...prev, [email]: 'idle' }));
      }
    } catch (err) {
      console.warn('⚠️ handleCopyInviteLink error:', err.message);
      showError('Failed to generate invite link. Please try again.');
      setCopyInviteState(prev => ({ ...prev, [email]: 'idle' }));
    }
  }, [selectedTeam, userTeamName]);

  // Invite user to team
  const handleInviteUser = async (userData) => {
    try {
      // Normalize email immediately to prevent case sensitivity bugs
      const normalizedEmail = userData.email.toLowerCase().trim();
            
      // Check if user already exists and their status - PREVENT duplicate invitations
      const emailDocId = getEmailDocumentId(normalizedEmail);
      const existingUser = users.find(u => u.id === emailDocId);
            
      if (existingUser) {
        const actualStatus = getActualUserStatus(existingUser);
                
        if (actualStatus === 'active') {
          showError(
            `User ${normalizedEmail} is already an active team member. To change their role or template, use the table below.`,
            { duration: 6000 }
          );
          return;
        } else if (actualStatus === 'invited') {
          showWarning(
            `User ${normalizedEmail} already has a pending invitation. They can sign up using the invitation that was previously sent.`,
            { duration: 6000 }
          );
          // Test-only: fire a fresh sendAgentInvitation CF call so the test runner can
          // capture the signup URL without needing to remove/re-invite the agent.
          if (process.env.REACT_APP_ENVIRONMENT === 'test') {
            const testTeamId = existingUser.teamId || userTeam;
            const testTeamName = existingUser.teamName || userTeamName || selectedTeam;
            (async () => {
              try {
                const { getFunctions, httpsCallable } = await import('firebase/functions');
                const fn = getFunctions();
                const sendInvitation = httpsCallable(fn, 'sendAgentInvitation', { timeout: 600000 });
                const result = await sendInvitation({ email: normalizedEmail, teamId: testTeamId, teamName: testTeamName });
                const signupUrl = result.data?.signupUrl;
                if (signupUrl) {
                  window.__testSignupUrl = signupUrl;
                  try { localStorage.setItem('__testSignupUrl', signupUrl); } catch { /* localStorage may be unavailable */ }
                }
              } catch (e) {
                console.warn('⚠️ [TEST] Could not refresh invitation URL:', e.message);
              }
            })();
          }
          return;
        }
      }
            
      // Add the invitedBy field to track who sent the invitation
      const userDataWithInviter = {
        ...userData,
        email: normalizedEmail,
        invitedBy: userEmail
      };
            
      // In TeamManager, automatically assign users to the appropriate team
      if (userRole === 'team_lead' && userTeam) {
        userDataWithInviter.teamId = userTeam;
      } else if (userRole === 'admin' && !userData.teamId && teams.length > 0) {
        userDataWithInviter.teamId = teams[0].id;
      }
            
      // inviteUser writes to Firestore immediately then fires off the CF asynchronously.
      // The onSignupUrl callback is called once the CF resolves (may take minutes on cold start).
      const capturedEmail = normalizedEmail; // capture for closure
      await inviteUser(db, firebaseConfig, userDataWithInviter, getEmailDocumentId, false, (signupUrl) => {
        setLastInviteLink(signupUrl);
        setLastInviteEmail(capturedEmail);
        setLinkCopied(false);
        // Expose for E2E test polling (test environment only).
        // Also write to localStorage so the value survives session-conflict page navigations.
        if (process.env.REACT_APP_ENVIRONMENT === 'test') {
          window.__testSignupUrl = signupUrl;
          try { localStorage.setItem('__testSignupUrl', signupUrl); } catch { /* localStorage may be unavailable */ }
        }
      });

      // Optimistically append the invited user so the row appears immediately.
      // loadData() will replace this with the real Firestore document.
      setUsers(prev => [
        ...prev,
        {
          id: emailDocId,
          email: normalizedEmail,
          role: userDataWithInviter.role || 'basic',
          status: 'invited',
          invitedBy: userEmail,
          templateId: userDataWithInviter.templateId || null,
          _optimistic: true,
        }
      ]);
      loadData();
            
      showSuccess(`Invitation created for ${normalizedEmail}!`, { duration: 5000 });
    } catch (error) {
      console.error('Error inviting user:', error);
      
      if (error.code === 'AGENT_LIMIT_REACHED') {
        const limitInfo = error.limitInfo;
        showError(
          'Agent limit reached (' + (limitInfo?.currentCount || 0) + '/' + (limitInfo?.limit || 0) + '). ' +
          'To add more team members, go to Settings > Subscription and purchase additional agent slots or upgrade your plan.',
          { duration: 8000 }
        );
      } else {
        showError(`Failed to invite user. ${error.message}`);
      }
    }
  };

  // Update user template
  const handleUpdateUserTemplate = async (userId, newTemplate) => {
    try {
      const user = users.find(u => u.id === userId);
      await updateUserTemplate(db, firebaseConfig, userId, newTemplate, user?.teamId || null);
      const templateName = allTemplates[newTemplate]?.name || 'No Template';
      showSuccess(`Template updated to "${templateName}" for ${user.email}`);
      loadData();
    } catch (error) {
      console.error('Error updating user template:', error);
      showError(`Failed to update template. ${error.message}`);
    }
  };

  // Update user role (inline editing - 9A)
  const handleUpdateUserRole = async (userId, newRole, email) => {
    try {
      setEditingRoleUserId(null);
      await updateUserRole(db, firebaseConfig, userId, newRole);

      // Refresh claims so the role change takes effect immediately
      try {
        console.log('🔄 Refreshing custom claims after role change for:', email);
        await refreshUserClaimsByEmail({ userEmail: email });
        console.log('✅ Custom claims refreshed successfully');
      } catch (claimsError) {
        console.error('❌ Error refreshing claims:', claimsError);
        // Don't fail the role update if claims refresh fails
      }

      const roleName = getRoleDisplayName(newRole);
      showSuccess(`Role updated to "${roleName}" for ${email}`);
      loadData();
    } catch (error) {
      console.error('Error updating user role:', error);
      showError(`Failed to update role. ${error.message}`);
    }
  };

  // Remove user from team - with confirmation
  const [userToRemove, setUserToRemove] = useState(null);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);

  const confirmRemoveUser = (user) => {
    setUserToRemove(user);
    setShowRemoveConfirm(true);
  };

  const handleRemoveUserFromTeam = async () => {
    if (!userToRemove) return;
    
    try {
      setLoading(true);
      setShowRemoveConfirm(false);
      
      console.log(`🔄 Removing user ${userToRemove.email} from team...`);
      await removeUserFromTeam(db, firebaseConfig, userToRemove.id, userToRemove.teamId || selectedTeam);
      
      showSuccess(`Successfully removed ${userToRemove.email} from the team. They can be re-invited to any team.`);
      console.log(`✅ User ${userToRemove.email} removed successfully`);
      
      setUserToRemove(null);
      loadData();
    } catch (error) {
      console.error('❌ Error removing user from team:', error);
      showError(`Failed to remove user: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Deactivate user
  const handleDeactivateUser = async (userId) => {
    try {
      setLoading(true);
            
      const appId = getArtifactProjectId();
      const user = users.find(u => u.id === userId);
      if (!user) {
        showError('User not found!');
        return;
      }
            
      // Find user's team and update their status to inactive
      const teamId = await findUserTeam(db, appId, userId, userTeam);
      if (!teamId) {
        showError('User\'s team not found!');
        return;
      }
            
      const teamUserRef = doc(db, firestorePaths.getUsersPath(appId, teamId), userId);
      await setDoc(teamUserRef, {
        status: 'inactive',
        deactivatedAt: new Date(),
        deactivatedBy: userEmail
      }, { merge: true });
            
      // Immediately refresh the user's custom claims to ensure they're signed out
      try {
        console.log('🔄 Refreshing custom claims for deactivated user:', user.email);
        await refreshUserClaimsByEmail({ userEmail: user.email });
        console.log('✅ Custom claims refreshed successfully');
                
        // Also force a token refresh for the current user if they're the one being deactivated
        // This handles the case where an admin deactivates themselves
        if (userEmail === user.email) {
          console.log('🔄 Current user was deactivated, forcing token refresh...');
          // The auth state listener will handle signing out when it detects inactive status
        }
      } catch (claimsError) {
        console.error('❌ Error refreshing claims:', claimsError);
        // Don't fail the deactivation if claims refresh fails
      }
            
      showSuccess(`User ${user.email} deactivated. They will be signed out automatically.`, { duration: 5000 });
      await loadData();
    } catch (error) {
      console.error('Error deactivating user:', error);
      showError(`Failed to deactivate user. ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Reactivate user
  const handleReactivateUser = async (userId) => {
    try {
      setLoading(true);
            
      const appId = getArtifactProjectId();
      const user = users.find(u => u.id === userId);
      if (!user) {
        showError('User not found!');
        return;
      }
            
      // Find user's team and update their status to active
      const teamId = await findUserTeam(db, appId, userId, userTeam);
      if (!teamId) {
        showError('User\'s team not found!');
        return;
      }
            
      const teamUserRef = doc(db, firestorePaths.getUsersPath(appId, teamId), userId);
      await setDoc(teamUserRef, {
        status: 'active',
        reactivatedAt: new Date(),
        reactivatedBy: userEmail
      }, { merge: true });
            
      // Immediately refresh the user's custom claims to restore access
      try {
        console.log('🔄 Refreshing custom claims for reactivated user:', user.email);
        await refreshUserClaimsByEmail({ userEmail: user.email });
        console.log('✅ Custom claims refreshed successfully');
      } catch (claimsError) {
        console.error('❌ Error refreshing claims:', claimsError);
        // Don't fail the reactivation if claims refresh fails
      }
            
      showSuccess(`User ${user.email} reactivated. They can log in now.`, { duration: 5000 });
      await loadData();
    } catch (error) {
      console.error('Error reactivating user:', error);
      showError(`Failed to reactivate user. ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Confirm deactivation before executing (9C)
  const confirmDeactivateUser = (user) => {
    setUserToDeactivate(user);
    setShowDeactivateConfirm(true);
  };

  const executeDeactivateUser = async () => {
    if (!userToDeactivate) return;
    setShowDeactivateConfirm(false);
    const userId = userToDeactivate.id;
    setUserToDeactivate(null);
    await handleDeactivateUser(userId);
  };

  // Bulk assign template to selected users
  const handleBulkAssignTemplate = async () => {
    if (selectedUsers.length === 0) {
      showWarning('Please select at least one user to assign a template to.');
      return;
    }

    try {
      const userIdToTeamId = users.reduce((acc, u) => {
        if (selectedUsers.includes(u.id) && u.teamId) acc[u.id] = u.teamId;
        return acc;
      }, {});
      const result = await bulkAssignTemplate(db, firebaseConfig, selectedUsers, bulkTemplate, userIdToTeamId);
            
      const templateName = allTemplates[bulkTemplate]?.name || 'No Template';
      if (result?.failed > 0) {
        showWarning(`Template "${templateName}" assigned to ${result.updated} user(s). ${result.failed} failed.`);
      } else {
        showSuccess(`Template "${templateName}" assigned to ${selectedUsers.length} user(s)!`);
      }
            
      setSelectedUsers([]);
      setBulkTemplate('example-script');
      setShowTemplateAssignment(false);
      loadData();
    } catch (error) {
      console.error('Error bulk assigning template:', error);
    }
  };

  // Toggle user selection for bulk operations
  const toggleUserSelection = (userId) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  // Filter users based on current filters AND selected team (for admins)
  const filteredUsers = users.filter(user => {
    const emailMatch = !filters.email || user.email.toLowerCase().includes(filters.email.toLowerCase());
    const roleMatch = !filters.role || user.role === filters.role;
    const statusMatch = !filters.status || getActualUserStatus(user) === filters.status;
    const teamMatch = !filters.team || (user.teamId && teams.find(t => t.id === user.teamId)?.name === filters.team);
        
    // Admin team selector filter (primary filter - overrides team filter dropdown)
    const selectedTeamMatch = !isAdmin || !selectedTeam || user.teamId === selectedTeam;
        
    return emailMatch && roleMatch && statusMatch && teamMatch && selectedTeamMatch;
  });

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      email: '',
      role: '',
      status: '',
      team: ''
    });
  };

  // Get unique values for filter dropdowns
  const uniqueRoles = [...new Set(users.map(u => u.role))];
  const uniqueStatuses = [...new Set(users.map(u => getActualUserStatus(u)))];
  const uniqueTeams = [...new Set(teams.map(t => t.name))];

  return (
    <>
      <div style={{ 
        backgroundColor: colors.neutral[50], 
        borderRadius: '0.5rem', 
        padding: spacing.lg, 
        width: '100%', 
        height: '90vh', 
        overflowY: 'auto', 
        minWidth: 0 
      }}>

        {(loading || !hasInitialized) && (
          <div className="text-center py-4">
            <LoadingSpinner size="medium" message="Loading..." />
          </div>
        )}

        {/* Main Content - Only show when not loading and initialized */}
        {!loading && hasInitialized && (
          <>
            {!userRole && (
              <div className="text-center py-4">
                <LoadingSpinner size="medium" message="Loading user role..." />
              </div>
            )}

            {/* Team info subtitle - shows selected team when admin is viewing a specific team */}
            <p className="text-sm text-gray-600 mb-4">
              {userRole === 'admin' && isViewingOtherTeam
                ? `Viewing team: ${userTeamName || 'Unknown'}`
                : userRole === 'admin'
                  ? `Managing all teams: ${teams.length} team${teams.length !== 1 ? 's' : ''}`
                  : userRole === 'team_lead' 
                    ? (userTeam 
                      ? `Managing your team: ${userTeamName || teams.find(t => t.id === userTeam)?.name || 'Unknown'}`
                      : `Teams you lead: ${teams.length} team${teams.length !== 1 ? 's' : ''}`)
                    : userRole === 'basic' && userTeam
                      ? `Team: ${userTeamName || teams.find(t => t.id === userTeam)?.name || 'Unknown'}`
                      : ''}
            </p>

            {/* Main Content */}
            {userRole && (
              <div className="space-y-4">
                {/* Agent Limit Status Banner */}
                {agentLimitInfo && !agentLimitInfo.isUnlimited && (
                  <div className={`p-3 rounded-lg flex items-center justify-between ${
                    !agentLimitInfo.canAdd 
                      ? 'bg-red-50 border border-red-200' 
                      : agentLimitInfo.remaining <= 1 
                        ? 'bg-yellow-50 border border-yellow-200'
                        : 'bg-blue-50 border border-blue-200'
                  }`}>
                    <div className="flex items-center gap-2">
                      <span className="text-lg">
                        {!agentLimitInfo.canAdd ? '🚫' : agentLimitInfo.remaining <= 1 ? '⚠️' : '👥'}
                      </span>
                      <span className={`font-medium ${
                        !agentLimitInfo.canAdd ? 'text-red-800' : 'text-gray-800'
                      }`}>
                        Agent Slots: {agentLimitInfo.currentCount}/{agentLimitInfo.limit}
                        {agentLimitInfo.remaining > 0 && ` (${agentLimitInfo.remaining} available)`}
                      </span>
                    </div>
                    {!agentLimitInfo.canAdd && (
                      <span className="text-sm text-red-600">
                        Upgrade plan or purchase more slots
                      </span>
                    )}
                  </div>
                )}
                
                {/* Lifetime/Unlimited Badge */}
                {agentLimitInfo?.isUnlimited && (
                  <div className="p-3 rounded-lg bg-purple-50 border border-purple-200 flex items-center gap-2">
                    <span className="text-lg">👑</span>
                    <span className="font-medium text-purple-800">
                      {agentLimitInfo.message || 'Unlimited agents'}
                    </span>
                    <span className="text-sm text-purple-600 ml-2">
                      ({agentLimitInfo.currentCount} active)
                    </span>
                  </div>
                )}
                
                {/* Trial Banner */}
                {agentLimitInfo?.isTrialing && (
                  <div className="p-3 rounded-lg bg-orange-50 border border-orange-200 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-lg">⚠️</span>
                      <span className="font-medium text-orange-800">
                        Upgrade your plan to add team members
                      </span>
                    </div>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => showInfo('Go to Settings > Subscription to upgrade your plan')}
                    >
                      Upgrade
                    </Button>
                  </div>
                )}
              
                {/* Signup link — full-width banner above actions so it can't be missed */}
                {lastInviteLink && (
                  <div className="p-3 bg-blue-50 border-2 border-blue-400 rounded-md shadow-sm">
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="flex-shrink-0 flex items-center justify-center w-7 h-7 bg-blue-600 rounded-full">
                          <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-1.102-4.828a4 4 0 015.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                          </svg>
                        </span>
                        <div className="min-w-0">
                          <span className="text-sm font-semibold text-blue-900">
                            Signup link for {lastInviteEmail}
                          </span>
                          <span className="text-xs text-blue-600 ml-2">Expires in 7 days</span>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setLastInviteLink(null)}
                        className="text-blue-400 hover:text-blue-600 p-0.5 flex-shrink-0"
                        aria-label="Dismiss"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                    <div className="flex items-center gap-2 mt-2">
                      <input
                        type="text"
                        readOnly
                        value={lastInviteLink}
                        className="flex-1 text-xs px-2.5 py-1.5 bg-white border border-blue-300 rounded text-gray-700 font-mono select-all"
                        onClick={(e) => e.target.select()}
                      />
                      <Button
                        variant={linkCopied ? 'ghost' : 'primary'}
                        size="sm"
                        onClick={() => {
                          navigator.clipboard.writeText(lastInviteLink);
                          setLinkCopied(true);
                          showSuccess('Signup link copied!', { duration: 2000 });
                        }}
                      >
                        {linkCopied ? 'Copied!' : 'Copy'}
                      </Button>
                      <a
                        href={`mailto:${encodeURIComponent(lastInviteEmail)}?subject=${encodeURIComponent("You're invited to join TreeGenie")}&body=${encodeURIComponent(`Hi,\n\nI've invited you to join our team on TreeGenie.\n\nClick the link below to create your account:\n${lastInviteLink}\n\nThis link expires in 7 days.`)}`}
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-blue-700 bg-white border border-blue-300 rounded hover:bg-blue-100 transition-colors whitespace-nowrap"
                        aria-label={`Send signup email to ${lastInviteEmail}`}
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        Email
                      </a>
                    </div>
                  </div>
                )}

                {/* Invite + Bulk Assignment — side by side */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                  <div className="lg:col-span-2">
                    <UserInviteForm
                      onSubmit={handleInviteUser}
                      teams={teams}
                      availableScripts={allTemplates}
                      showRoleSelector={true}
                      showTeamSelector={false}
                      submitLabel="Invite User to Team"
                      currentUserRole={userRole}
                      disabled={agentLimitInfo && !agentLimitInfo.canAdd && !agentLimitInfo.isUnlimited}
                      disabledMessage={agentLimitInfo?.isTrialing 
                        ? 'Upgrade to a paid plan to add team members' 
                        : 'Agent limit reached (' + (agentLimitInfo?.currentCount || 0) + '/' + (agentLimitInfo?.limit || 0) + '). Upgrade your plan or purchase additional agent slots.'
                      }
                    />
                  </div>

                  {/* Bulk Template Assignment — compact sidebar */}
                  {canManageUsers() && (
                    <div className="bg-gray-50 border border-gray-200 p-3 rounded-md">
                      <button
                        type="button"
                        onClick={() => setShowTemplateAssignment(!showTemplateAssignment)}
                        className="w-full flex items-center justify-between text-sm font-medium text-gray-700 hover:text-gray-900"
                      >
                        <span>Bulk Template Assignment</span>
                        <svg className={`w-4 h-4 transition-transform ${showTemplateAssignment ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                      {showTemplateAssignment && (
                        <div className="mt-3 space-y-2">
                          <TemplateSelector
                            value={bulkTemplate}
                            onChange={(e) => setBulkTemplate(e.target.value)}
                            availableScripts={allTemplates}
                          />
                          <Button
                            variant="primary"
                            size="sm"
                            fullWidth
                            onClick={() => showTemplateWarning('bulkAssignTemplate', {})}
                            disabled={selectedUsers.length === 0}
                          >
                            Assign to {selectedUsers.length} Selected
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Filter Controls */}
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <div className="flex flex-wrap items-center gap-4">
                    <h4 className="font-medium text-gray-700">Filter Team Members</h4>
                                
                    {/* Email Filter */}
                    <div className="flex-1 min-w-48">
                      <input
                        type="text"
                        placeholder="Filter by email..."
                        value={filters.email}
                        onChange={(e) => setFilters(prev => ({ ...prev, email: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                                
                    {/* Role Filter */}
                    <div className="min-w-32">
                      <select
                        value={filters.role}
                        onChange={(e) => setFilters(prev => ({ ...prev, role: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">All Roles</option>
                        {uniqueRoles.map(role => (
                          <option key={role} value={role}>
                            {getRoleDisplayName(role)}
                          </option>
                        ))}
                      </select>
                    </div>
                                
                    {/* Status Filter */}
                    <div className="min-w-32">
                      <select
                        value={filters.status}
                        onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">All Statuses</option>
                        {uniqueStatuses.map(status => (
                          <option key={status} value={status}>
                            {getStatusDisplayName(status)}
                          </option>
                        ))}
                      </select>
                    </div>
                                
                    {/* Team Filter */}
                    <div className="min-w-32">
                      <select
                        value={filters.team}
                        onChange={(e) => setFilters(prev => ({ ...prev, team: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">All Teams</option>
                        {uniqueTeams.map(team => (
                          <option key={team} value={team}>
                            {team}
                          </option>
                        ))}
                      </select>
                    </div>
                                
                    {/* Clear Filters Button */}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={clearFilters}
                    >
                                    Clear Filters
                    </Button>
                                
                    {/* Results Count */}
                    <div className="text-sm text-gray-500">
                                    Showing {filteredUsers.length} of {users.length} team members
                    </div>
                  </div>
                            
                  {/* No Results Message */}
                  {filteredUsers.length === 0 && users.length > 0 && (
                    <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-md">
                      <div className="flex items-center space-x-2">
                        <div className="text-yellow-600">🔍</div>
                        <div className="text-yellow-800">
                          <strong>No team members found</strong> with the current filters. 
                                            Try adjusting your search criteria or 
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={clearFilters}
                            className="underline ml-1"
                          >
                                                clear all filters
                          </Button>.
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Users Table */}
                <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          {showTemplateAssignment && (
                            <th className="px-3 sm:px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              <input
                                type="checkbox"
                                checked={selectedUsers.length === filteredUsers.length && filteredUsers.length > 0}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedUsers(filteredUsers.map(u => u.id));
                                  } else {
                                    setSelectedUsers([]);
                                  }
                                }}
                                className="rounded border-gray-300"
                              />
                            </th>
                          )}
                          <th className="px-3 sm:px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User / Email</th>
                          <th className="px-3 sm:px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                          <th className="px-3 sm:px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Template</th>
                          <th className="px-3 sm:px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                          <th className="px-3 sm:px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {filteredUsers.map(user => (
                          <tr key={user.id} className="hover:bg-gray-50">
                            {showTemplateAssignment && (
                              <td className="px-3 sm:px-4 py-2 whitespace-nowrap">
                                <input
                                  type="checkbox"
                                  checked={selectedUsers.includes(user.id)}
                                  onChange={() => toggleUserSelection(user.id)}
                                  className="rounded border-gray-300"
                                />
                              </td>
                            )}
                            <td className="px-3 sm:px-4 py-2 whitespace-nowrap">
                              <div className="text-sm font-medium text-gray-900">
                                {(user.firstName || user.lastName) ? formatUserName(user) : user.email}
                              </div>
                              {(user.firstName || user.lastName) && (
                                <div className="text-xs text-gray-500">{user.email}</div>
                              )}
                            </td>
                            <td className="px-3 sm:px-4 py-2 whitespace-nowrap">
                              {canManageUsers() && editingRoleUserId === user.id ? (
                                <select
                                  value={user.role || 'basic'}
                                  onChange={(e) => handleUpdateUserRole(user.id, e.target.value, user.email)}
                                  onBlur={() => setEditingRoleUserId(null)}
                                  autoFocus
                                  className="text-sm border border-blue-400 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                  <option value="basic">{getRoleDisplayName('basic')}</option>
                                  <option value="team_lead">{getRoleDisplayName('team_lead')}</option>
                                  {userRole === 'admin' && <option value="admin">{getRoleDisplayName('admin')}</option>}
                                </select>
                              ) : (
                                <span
                                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleBadge(user.role || 'basic')} ${canManageUsers() ? 'cursor-pointer hover:ring-2 hover:ring-blue-300 transition-shadow' : ''}`}
                                  onClick={() => canManageUsers() && setEditingRoleUserId(user.id)}
                                  role={canManageUsers() ? 'button' : undefined}
                                  tabIndex={canManageUsers() ? 0 : undefined}
                                  onKeyDown={(e) => {
                                    if (canManageUsers() && (e.key === 'Enter' || e.key === ' ')) {
                                      e.preventDefault();
                                      setEditingRoleUserId(user.id);
                                    }
                                  }}
                                  title={canManageUsers() ? 'Click to change role' : ''}
                                >
                                  {getRoleDisplayName(user.role || 'basic')}
                                </span>
                              )}
                            </td>

                            <td className="px-3 sm:px-4 py-2 whitespace-nowrap">
                              {(() => {
                                // Get the user's template - BACKWARD COMPAT: check both defaultTemplate (new) and assignedTemplate (old)
                                const userTemplateId = user.defaultTemplate || user.assignedTemplate || 'example-script';
                                const templateData = allTemplates[userTemplateId];
                                const templateDisplayName = templateData?.name || 'Example Script (Demo)';
                                const roleLabel = user.role === 'basic' ? 'Assigned' : user.role === 'team_lead' ? 'Default' : 'Template';

                                // Inline editing mode: show dropdown
                                if (canManageUsers() && editingTemplateUserId === user.id) {
                                  return (
                                    <select
                                      value={userTemplateId}
                                      onChange={(e) => {
                                        setEditingTemplateUserId(null);
                                        handleUpdateUserTemplate(user.id, e.target.value);
                                      }}
                                      onBlur={() => setEditingTemplateUserId(null)}
                                      autoFocus
                                      className="text-sm border border-blue-400 rounded px-2 py-1 w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    >
                                      <option value="example-script">Example Script (Demo)</option>
                                      {Object.entries(allTemplates)
                                        .filter(([id]) => id !== 'example-script')
                                        .map(([id, script]) => (
                                          <option key={id} value={id}>
                                            {script.name}
                                          </option>
                                        ))}
                                    </select>
                                  );
                                }

                                // Display mode: clickable for managers, read-only otherwise
                                return (
                                  <div
                                    className={`text-sm ${canManageUsers() ? 'cursor-pointer hover:bg-blue-50 rounded px-2 py-1 border border-transparent hover:border-blue-300 transition-colors' : 'px-2 py-1'}`}
                                    onClick={() => canManageUsers() && setEditingTemplateUserId(user.id)}
                                    role={canManageUsers() ? 'button' : undefined}
                                    tabIndex={canManageUsers() ? 0 : undefined}
                                    onKeyDown={(e) => {
                                      if (canManageUsers() && (e.key === 'Enter' || e.key === ' ')) {
                                        e.preventDefault();
                                        setEditingTemplateUserId(user.id);
                                      }
                                    }}
                                    title={canManageUsers() ? 'Click to change template' : ''}
                                  >
                                    <div className="text-gray-900">{templateDisplayName}</div>
                                    <div className="text-xs text-gray-500">{roleLabel}</div>
                                  </div>
                                );
                              })()}
                            </td>
                            <td className="px-3 sm:px-4 py-2 whitespace-nowrap">
                              <div className="flex flex-col space-y-1">
                                {(() => {
                                  const actualStatus = getActualUserStatus(user);
                                  return (
                                    <>
                                      <span className={getStatusBadge(actualStatus)}>
                                        {getStatusDisplayName(actualStatus)}
                                      </span>
                                      {actualStatus === 'invited' && user.invitedBy && (
                                        <div className="text-xs text-gray-500">
                                                                            Invited by: {user.invitedBy}
                                        </div>
                                      )}
                                      {actualStatus === 'active' && user.invitedBy && (
                                        <div className="text-xs text-gray-500">
                                                                            Invited by: {user.invitedBy}
                                        </div>
                                      )}
                                    </>
                                  );
                                })()}
                              </div>
                            </td>
                            <td className="px-3 sm:px-4 py-2 whitespace-nowrap text-sm text-gray-500">
                              <div className="flex flex-col space-y-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => confirmRemoveUser(user)}
                                  disabled={!canManageUsers()}
                                  className="text-red-600 text-xs"
                                >
                                  Remove from Team
                                </Button>
                                {getActualUserStatus(user) === 'inactive' ? (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleReactivateUser(user.id)}
                                    disabled={!canManageUsers()}
                                    className="text-green-600 text-xs"
                                  >
                                    Reactivate User
                                  </Button>
                                ) : getActualUserStatus(user) === 'invited' ? (
                                  // Invited users waiting for signup — allow re-sharing the invite link
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleCopyInviteLink(user)}
                                    disabled={copyInviteState[user.email || user.id] === 'loading'}
                                    className="text-blue-600 text-xs"
                                    title="Generate and copy the signup link to clipboard"
                                  >
                                    {copyInviteState[user.email || user.id] === 'loading'
                                      ? '⏳ Getting link...'
                                      : copyInviteState[user.email || user.id] === 'copied'
                                        ? '✅ Copied!'
                                        : '🔗 Copy Invite Link'}
                                  </Button>
                                ) : (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => confirmDeactivateUser(user)}
                                    disabled={!canManageUsers()}
                                    className="text-orange-600 text-xs"
                                  >
                                                                Deactivate User
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

      </div>

      {/* Warning Dialog */}
      {showWarningDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-60" role="presentation" onKeyDown={(e) => { if (e.key === 'Escape') setShowWarningDialog(false); }}>
          <div className="bg-white rounded-lg p-4 max-w-md w-full mx-4" role="dialog" aria-modal="true" aria-labelledby="warning-dialog-title">
            <h3 id="warning-dialog-title" className="text-lg font-semibold text-gray-800 mb-4">Confirm Template Assignment</h3>
            <p className="text-gray-600 mb-4">
              {warningAction === 'updateUserTemplate' && (
                'Are you sure you want to change the template for this user? This will affect their script flow as soon as they refresh the app in their browser. Please ask them to refresh their browser to see the changes.'
              )}
              {warningAction === 'bulkAssignTemplate' && (
                `Are you sure you want to assign the "${allTemplates[bulkTemplate]?.name || 'No Template'}" template to ${selectedUsers.length} user(s)? This will affect their script flow once they refresh their browser.`
              )}
            </p>
            <div className="flex space-x-3">
              <Button
                variant="outline"
                size="md"
                onClick={() => setShowWarningDialog(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="md"
                onClick={executeWarningAction}
                className="flex-1"
              >
                Confirm
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Remove User Confirmation Dialog */}
      {showRemoveConfirm && userToRemove && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]" role="presentation" onKeyDown={(e) => { if (e.key === 'Escape') { setShowRemoveConfirm(false); setUserToRemove(null); } }}>
          <div className="bg-white rounded-lg p-4 max-w-md w-full mx-4 shadow-xl" role="dialog" aria-modal="true" aria-labelledby="remove-user-dialog-title">
            <h3 id="remove-user-dialog-title" className="text-lg font-semibold text-gray-800 mb-4">Remove User from Team?</h3>
            <p className="text-gray-600 mb-4">
              Are you sure you want to remove <strong>{userToRemove.email}</strong> from the team?
            </p>
            <p className="text-sm text-gray-500 mb-4">
              This will revoke their access to the team&apos;s scripts and data. They can be re-invited later if needed.
            </p>
            <div className="flex space-x-3">
              <Button
                variant="outline"
                size="md"
                onClick={() => {
                  setShowRemoveConfirm(false);
                  setUserToRemove(null);
                }}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                size="md"
                onClick={handleRemoveUserFromTeam}
                className="flex-1"
              >
                Remove User
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Deactivate User Confirmation Dialog (9C) */}
      <ConfirmDialog
        isOpen={showDeactivateConfirm}
        onClose={() => {
          setShowDeactivateConfirm(false);
          setUserToDeactivate(null);
        }}
        onConfirm={executeDeactivateUser}
        title="Deactivate User?"
        message={`This will immediately sign out ${userToDeactivate?.email || 'this user'}. Continue?`}
        confirmLabel="Deactivate"
        confirmVariant="danger"
      />

    </>
  );
};

export default TeamManager;
